import React from 'react';
import ReactS3Uploader, { S3Response } from 'react-s3-uploader';
import LocalSpinner from 'components/spinners/local';
import ErrorMessage from 'components/errorMessage';
import { FieldValues, UseFormProps, UseFormRegister } from 'react-hook-form';
import { APPLICATION_CENTER } from 'state/applicationCenter/type';
import { Label } from 'components/ui/label';
import { Icon } from 'components/ui/icon';
import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

interface IProps {
  name: APPLICATION_CENTER.docType;
  showStatus: boolean;
  getSignedUrl: (
    file: File,
    callback: (params: S3Response) => void,
    fileType: APPLICATION_CENTER.docType
  ) => void;
  key: string;
  onUploadFinish: (
    result: S3Response,
    file: File,
    fileType: APPLICATION_CENTER.docType
  ) => void;
  register: UseFormRegister<FieldValues>;
  errors: UseFormProps['errors'];
  onError: (message: string, fileType: APPLICATION_CENTER.docType) => void;
  onProgress: (
    percent: number,
    status: string,
    file: File,
    fileType: APPLICATION_CENTER.docType
  ) => void;
  uploadedFile: File | undefined;
  activeFile: string | undefined;
  clearFile: (formType: APPLICATION_CENTER.docType) => void;
  fileStatus: string;
  percentUploaded: number;
  subLabel?: string | JSX.Element;
  required?: boolean;
  fieldConfig: Record<string, APPLICATION_CENTER.FieldConfig>;
  activeField: string;
  multipleFiles?: boolean;
}

const DocumentUpload = ({
  showStatus,
  getSignedUrl,
  onUploadFinish,
  register,
  onError,
  key,
  onProgress,
  name,
  uploadedFile,
  activeFile,
  errors,
  clearFile,
  fileStatus,
  percentUploaded,
  subLabel,
  required = true,
  fieldConfig,
  activeField,
  multipleFiles,
}: IProps): JSX.Element => {
  const field: string = name as string;
  const { ref, ...registerField } = register(name, {
    validate: {
      required: (value) => {
        return required ? !(value?.length <= 0) : true;
      },
    },
  });

  const showCrossBtn = () => {
    if (!uploadedFile) return null;

    return (
      <Button
        type="button"
        variant="ghost"
        size="icon"
        data-testid="crossBtn"
        onClick={() => clearFile(name)}
        className="ml-2 text-gray-blue-60 hover:text-gray-blue-80 hover:bg-gray-blue-5"
      >
        <Icon name="x" size={16} />
      </Button>
    );
  };

  const showSpinner = () => {
    if (fileStatus === 'Upload completed') {
      return (
        <Icon
          data-testid="successIcon"
          name="check-circle"
          className="text-green-500"
          size={24}
          aria-label="Upload complete"
        />
      );
    }
    return <LocalSpinner size="sm" color="primary" isLoading />;
  };

  return (
    <>
      <Label
        htmlFor={fieldConfig[activeField].name}
        className={cn(
          'text-sm font-medium text-gray-blue-80 mb-1 block',
          showStatus && 'cursor-not-allowed text-gray-blue-30 opacity-100'
        )}
      >
        {fieldConfig[activeField].label}
        {fieldConfig[activeField].isRequired && (
          <span className="text-red-500 ml-0.5">*</span>
        )}
      </Label>
      <div className="flex items-center bg-gray-blue-0 border border-gray-blue-20 rounded">
        <ReactS3Uploader
          data-testid={name}
          id={name}
          disabled={showStatus}
          getSignedUrl={(file: File, callback: (params: S3Response) => void) =>
            getSignedUrl(file, callback, name)
          }
          onFinish={(result: S3Response, file: File) =>
            onUploadFinish(result, file, name)
          }
          key={key || ''}
          multiple={multipleFiles}
          className={cn(
            'flex-1 h-9 w-full max-w-4xl cursor-pointer text-sm text-gray-blue-50',
            'file:mr-3 file:px-4 file:py-2 file:border-0 file:border-r file:border-gray-blue-20 file:bg-gray-blue-0 file:text-sm file:font-medium file:text-gray-blue-80 file:cursor-pointer file:shadow-sm',
            'hover:bg-gray-blue-2 file:hover:bg-gray-blue-2',
            errors?.[name] && 'border-red-400',
            showStatus && 'cursor-not-allowed text-gray-blue-30 opacity-100'
          )}
          inputRef={ref}
          {...registerField}
          accept=".pdf"
          onProgress={(percent: number, status: string, file: File) =>
            onProgress(percent, status, file, name)
          }
          onError={(message: string) => onError(message, name)}
          uploadRequestHeaders={{}}
          contentDisposition="auto"
        />
        {showCrossBtn()}
      </div>
      {activeFile === name && showStatus && (
        <div className="flex items-center justify-between pt-2">
          <span className="text-sm font-normal leading-5 text-gray-blue-60">
            {fileStatus} - {percentUploaded}% uploaded
          </span>
          {showSpinner()}
        </div>
      )}
      {subLabel && (
        <span className="text-sm font-normal leading-5 text-gray-blue-60 max-w-4xl block mt-2">
          {subLabel}
        </span>
      )}
      <div>
        {errors?.[field] && (
          <span className="flex items-center gap-1 pt-2">
            <Icon name="alert-circle" className="text-red-500" size={16} />
            <ErrorMessage
              isValidation
              message={
                typeof errors?.[field]?.message === 'string'
                  ? (errors?.[field]?.message as string)
                  : 'Please upload the required documents'
              }
            />
          </span>
        )}
      </div>
    </>
  );
};

export default DocumentUpload;
